import React from 'react';
import {
  type DocumentElement as DocumentElementType,
  type MdBlock,
  type MdInline,
  parseMarkdown,
} from '@eaco/forms-core';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';

interface Props {
  element: DocumentElementType;
}

const HEADING_CLASS: Record<number, string> = {
  1: 'text-[16px] font-extrabold text-[var(--forms-ink)]',
  2: 'text-[15px] font-extrabold text-[var(--forms-ink)]',
  3: 'text-[14px] font-bold text-[var(--forms-ink)]',
  4: 'text-[13.5px] font-bold text-[var(--forms-ink)]',
  5: 'text-[13px] font-bold text-[var(--forms-ink)]',
  6: 'text-[13px] font-bold text-[var(--forms-muted)]',
};

const renderInlines = (inlines: MdInline[]): React.ReactNode =>
  inlines.map((run, i) => {
    switch (run.type) {
      case 'bold':
        return (
          <strong key={i} className="font-bold text-[var(--forms-ink)]">
            {run.value}
          </strong>
        );
      case 'italic':
        return (
          <em key={i} className="italic">
            {run.value}
          </em>
        );
      case 'code':
        return (
          <code
            key={i}
            className="rounded-[3px] bg-[#f1f3f6] px-[4px] py-[1px] font-mono text-[0.9em] text-[#334]"
          >
            {run.value}
          </code>
        );
      case 'link':
        return (
          <a
            key={i}
            href={run.href}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-[var(--forms-accent,var(--forms-accent-default))] underline"
          >
            {run.value}
          </a>
        );
      default:
        return <React.Fragment key={i}>{run.value}</React.Fragment>;
    }
  });

const renderBlock = (block: MdBlock, i: number): React.ReactNode => {
  switch (block.type) {
    case 'heading':
      return (
        <div key={i} className={HEADING_CLASS[block.level] || HEADING_CLASS[3]}>
          {renderInlines(block.inlines)}
        </div>
      );
    case 'list':
      return (
        <ul
          key={i}
          className="list-disc space-y-[3px] pl-[20px] text-[14px] leading-[1.6] text-[#4a5262]"
        >
          {block.items.map((item, j) => (
            <li key={j}>{renderInlines(item)}</li>
          ))}
        </ul>
      );
    case 'ordered-list':
      return (
        <ol
          key={i}
          className="list-decimal space-y-[3px] pl-[20px] text-[14px] leading-[1.6] text-[#4a5262]"
        >
          {block.items.map((item, j) => (
            <li key={j}>{renderInlines(item)}</li>
          ))}
        </ol>
      );
    case 'todo':
      return (
        <ul key={i} className="space-y-[3px] text-[14px] leading-[1.6] text-[#4a5262]">
          {block.items.map((item, j) => (
            <li key={j} className="flex items-start gap-[8px]">
              {/* Display-only, like the rest of this element: the box shows
                  the state the author wrote, and ticking it is not a thing
                  a Rich Text block can do. */}
              <input
                type="checkbox"
                checked={item.checked}
                readOnly
                disabled
                className="mt-[4px] h-[13px] w-[13px] flex-none"
              />
              <span>{renderInlines(item.inlines)}</span>
            </li>
          ))}
        </ul>
      );
    case 'quote':
      return (
        <blockquote
          key={i}
          className="border-l-[3px] border-[#d8dce5] pl-[12px] text-[14px] italic leading-[1.6] text-[#6b7280]"
        >
          {renderInlines(block.inlines)}
        </blockquote>
      );
    case 'table':
      return (
        // Its own scroll container: a wide table must not make the whole
        // form scroll sideways.
        <div key={i} className="overflow-x-auto">
          <table className="w-full border-collapse text-[14px] leading-[1.6] text-[#4a5262]">
            <thead>
              <tr>
                {block.columns.map((c, j) => (
                  <th
                    key={j}
                    className="border-b-[1px] border-[#d8dce5] px-[8px] py-[6px] text-left font-[700]"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, j) => (
                <tr key={j}>
                  {row.map((cell, k) => (
                    <td key={k} className="border-b-[1px] border-[#eceef2] px-[8px] py-[6px]">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'divider':
      return <hr key={i} className="border-0 border-t-[1px] border-[#e2e5ea]" />;
    case 'image':
      return (
        // eslint-disable-next-line jsx-a11y/alt-text -- alt comes from the author
        <img
          key={i}
          src={block.url}
          alt={block.alt}
          className="max-w-full rounded-[6px]"
        />
      );
    case 'code':
      return (
        <pre
          key={i}
          className="overflow-x-auto rounded-[6px] bg-[#f5f5f5] p-[12px] text-[13px] leading-[1.5] text-[#1f2430]"
        >
          <code>{block.text}</code>
        </pre>
      );
    default:
      return (
        <p key={i} className="text-[14px] leading-[1.6] text-[#4a5262]">
          {renderInlines(block.inlines)}
        </p>
      );
  }
};

/**
 * Rich Text (Elements Library): a static formatted block — headings, bold,
 * italic, lists and links — authored as a Markdown subset. No value;
 * display-only.
 */
const RichText: React.FC<Props> = ({ element }) => {
  const data = element.data as { content?: string; hidden?: boolean };
  const blocks = parseMarkdown(data?.content || '');

  return (
    <div className="space-y-2">
      {blocks.length === 0 ? (
        <p className="text-[13px] italic text-[var(--forms-muted-soft)]">
          Rich text block — add formatted content in the element settings.
        </p>
      ) : (
        <div className="space-y-[7px]">{blocks.map(renderBlock)}</div>
      )}
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default RichText;
